import { useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';

import { ProtectedComponent } from '@/components/ProtectedComponent';
import { DropdownAction } from '@/components/ui/dropdown-actions';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';

import { useDeleteBatchCallMutation } from '@/query/scheduler.query';

import { Permissions } from '@/constants';

import { BatchCallUpdateSheet } from './batch-call-update-sheet';

import useAlertStore from '@/store/alert.store';
import type { BatchCall } from '@/types/scheduler.type';

type Props = {
  call: BatchCall;
};

export const RowAction = ({ call }: Props) => {
  const openAlert = useAlertStore(s => s.openAlert);
  const { mutate: deleteBatchCall } = useDeleteBatchCallMutation();
  const [editOpen, setEditOpen] = useState(false);

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    openAlert({
      title: 'Delete Batch Call',
      description: `Are you sure you want to delete "${call.batchName}"? This action cannot be undone.`,
      type: 'destructive',
      confirmText: 'Delete',
      onConfirm: () => deleteBatchCall(call.id),
    });
  };

  return (
    // Stop click events (including from the Sheet portal) from bubbling to the DataTable row's onRowClick
    <div onClick={e => e.stopPropagation()}>
      <ProtectedComponent
        requiredPermissions={[Permissions.BatchCallCreateUpdate, Permissions.BatchCallDelete]}
      >
        <DropdownAction>
          <ProtectedComponent requiredPermissions={[Permissions.BatchCallCreateUpdate]}>
            <DropdownMenuItem
              className="gap-2"
              onClick={e => {
                e.stopPropagation();
                setEditOpen(true);
              }}
            >
              <Pencil className="h-4 w-4" />
              Edit
            </DropdownMenuItem>
          </ProtectedComponent>
          <ProtectedComponent requiredPermissions={[Permissions.BatchCallDelete]}>
            <DropdownMenuItem
              className="text-destructive focus:text-destructive gap-2"
              onClick={handleDelete}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
              Delete
            </DropdownMenuItem>
          </ProtectedComponent>
        </DropdownAction>
      </ProtectedComponent>

      <BatchCallUpdateSheet call={call} open={editOpen} onClose={() => setEditOpen(false)} />
    </div>
  );
};
